import { useState, useEffect, useCallback, useMemo } from "react";
import { PageHeader, StatCard } from "@/components/Primitives";
import { StatusChip } from "@/components/StatusChip";
import { fmtMoney } from "@/lib/format";
import api, { formatErr } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Clock, CheckCircle2, DollarSign, CalendarRange, TrendingUp } from "lucide-react";

export default function Commissions() {
  const { can, tenant, user } = useAuth();
  const cur = tenant?.currency || "CAD";
  const isManager = can("tenant_owner", "platform_owner", "sales_manager");
  const isRep = user?.role === "sales_rep";
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get("/commissions").then((r) => setRows(r.data.items || [])), []);
  useEffect(() => { load(); }, [load]);

  const act = async (id, status) => {
    try { await api.post(`/commissions/${id}/action`, { status }); toast.success(`Marked ${status}`); load(); }
    catch (e) { toast.error(formatErr(e.response?.data?.detail)); }
  };

  const totals = useMemo(() => {
    const t = { Projected: 0, Pending: 0, Approved: 0, Paid: 0, month: 0 };
    const now = new Date();
    const monthKey = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
    rows.forEach((c) => {
      const amt = Number(c.calculated_amount || 0);
      t[c.status] = (t[c.status] || 0) + amt;
      if (c.due_month) {
        if (c.due_month === monthKey) t.month += amt;
      } else {
        const sd = c.sale_date ? new Date(c.sale_date) : null;
        if (sd && sd.getMonth() === now.getMonth() && sd.getFullYear() === now.getFullYear()) t.month += amt;
      }
    });
    return t;
  }, [rows]);

  const title = isRep ? "My Commissions" : "Commissions";
  const subtitle = isRep
    ? "Your commission earnings and projected monthly payouts. You can only see commissions assigned to you."
    : "Review projected, pending, approved and paid commission payouts.";

  return (
    <div data-testid="page-commissions">
      <PageHeader title={title} subtitle={subtitle} />

      {isRep && (
        <div className="mb-5 rounded-xl border border-cyan-200 bg-cyan-50/70 px-4 py-3 text-sm text-slate-600">
          <span className="font-semibold text-slate-800">Projected</span> amounts are expected future commissions based on your contract schedule. They move to Pending when the contract month arrives, then management approves and marks them paid.
        </div>
      )}

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5 mb-6">
        <StatCard label="This Month" value={fmtMoney(totals.month, cur)} icon={CalendarRange} tone="cyan" />
        <StatCard label="Projected" value={fmtMoney(totals.Projected, cur)} icon={TrendingUp} tone="cyan" />
        <StatCard label="Pending" value={fmtMoney(totals.Pending, cur)} icon={Clock} tone="orange" />
        <StatCard label="Approved" value={fmtMoney(totals.Approved, cur)} icon={CheckCircle2} tone="green" />
        <StatCard label="Paid" value={fmtMoney(totals.Paid, cur)} icon={DollarSign} tone="green" />
      </div>

      <div className="hidden md:block cc-card overflow-x-auto">
        <table className="w-full text-sm">
          <thead><tr className="border-b bg-slate-50/70 text-left cc-overline">
            <th className="px-4 py-3">Contract</th>
            <th className="px-4 py-3">Advertiser</th>
            {!isRep && <th className="px-4 py-3">Salesperson</th>}
            <th className="px-4 py-3">Due</th>
            <th className="px-4 py-3">Rule</th>
            <th className="px-4 py-3">Amount</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-b border-slate-100 last:border-0 hover:bg-[#00C9E0]/[0.04]" data-testid={`comm-${c.id}`}>
                <td className="px-4 py-3.5 font-semibold">{c.contract_number}</td>
                <td className="px-4 py-3.5">{c.business_name}</td>
                {!isRep && <td className="px-4 py-3.5 text-slate-500">{c.salesperson_name}</td>}
                <td className="px-4 py-3.5 text-slate-500">{c.due_month || "—"}</td>
                <td className="px-4 py-3.5 text-slate-500">{c.commission_rule || (c.commission_percentage ? `${c.commission_percentage}%` : "Fixed")}</td>
                <td className="px-4 py-3.5 font-bold text-[#0094A8] tnum">{fmtMoney(c.calculated_amount, cur)}</td>
                <td className="px-4 py-3.5"><StatusChip value={c.status} /></td>
                <td className="px-4 py-3.5 text-right">
                  {isManager && c.status === "Pending" && <Button size="sm" variant="outline" onClick={() => act(c.id, "Approved")} data-testid={`approve-${c.id}`}>Approve</Button>}
                  {isManager && c.status === "Approved" && <Button size="sm" onClick={() => act(c.id, "Paid")} data-testid={`pay-${c.id}`}>Mark Paid</Button>}
                </td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={isRep ? 7 : 8} className="px-4 py-10 text-center text-slate-400">No commissions yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <div className="md:hidden space-y-3">
        {rows.length === 0 && <div className="cc-card p-6 text-center text-sm text-slate-400">No commissions yet.</div>}
        {rows.map((c) => (
          <div key={c.id} className="cc-card p-4" data-testid={`comm-${c.id}`}>
            <div className="flex items-start justify-between gap-3">
              <div><p className="font-semibold text-sm">{c.contract_number}</p><p className="text-xs text-slate-400">{c.business_name}</p></div>
              <StatusChip value={c.status} />
            </div>
            <div className="mt-2 flex items-center justify-between gap-3">
              <span className="text-xs text-slate-400">{c.due_month ? `Due ${c.due_month}` : (isRep ? c.commission_rule : c.salesperson_name)}</span>
              <span className="font-bold text-[#0094A8] tnum">{fmtMoney(c.calculated_amount, cur)}</span>
            </div>
            {c.commission_rule && <div className="mt-1 text-[11px] text-slate-400">{c.commission_rule}</div>}
            {isManager && (c.status === "Pending" || c.status === "Approved") && (
              <div className="mt-3">
                {c.status === "Pending" && <Button size="sm" variant="outline" className="w-full" onClick={() => act(c.id, "Approved")} data-testid={`approve-${c.id}`}>Approve</Button>}
                {c.status === "Approved" && <Button size="sm" className="w-full" onClick={() => act(c.id, "Paid")} data-testid={`pay-${c.id}`}>Mark Paid</Button>}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
